import React, { useRef, useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';

export default function LocationAutocomplete({ 
  value, 
  onChange, 
  onPlaceSelect,
  placeholder = "Enter location",
  icon = "pickup" // "pickup" or "dropoff"
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  useEffect(() => {
    // Check if Google Maps is loaded
    const checkGoogleMaps = () => {
      if (window.google?.maps?.places) {
        setIsGoogleLoaded(true);
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkGoogleMaps()) return;

    // If not loaded, wait and retry
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 500);

    // Cleanup after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.google?.maps?.places) {
        console.error('Google Maps failed to load after 10 seconds');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!isGoogleLoaded || !inputRef.current) return;

    // Initialize Google Places Autocomplete
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'pk' },
      fields: ['formatted_address', 'geometry', 'name', 'address_components']
    });

    // Listen for place selection
    const listener = autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      
      if (!place.geometry) {
        console.error('No geometry for this place');
        return;
      }

      // Store FULL address - don't let it shorten!
      const fullAddress = place.formatted_address || place.name;
      
      const locationData = {
        address: fullAddress,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      console.log('Location selected:', locationData);
      
      // Call parent's handler
      onPlaceSelect(locationData);
      
      // IMPORTANT: Set the input value to full address
      // This prevents Google from shortening it
      if (inputRef.current) {
        inputRef.current.value = fullAddress;
      }
    });

    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [isGoogleLoaded, onPlaceSelect]);

  // If Google Maps not loaded, show disabled input
  if (!isGoogleLoaded) {
    return (
      <div className="relative">
        <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300`} />
        <input
          type="text"
          placeholder="Loading maps..."
          disabled
          className="w-full pl-10 pr-4 py-3 border rounded-lg bg-gray-100 text-gray-500"
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
        icon === 'pickup' ? 'text-emerald-500' : 'text-red-500'
      }`} />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500"
      />
    </div>
  );
}